/**
 * shareUtils.js - 배틀 공유 및 메타 데이터 유틸
 */

/**
 * 배틀 정보를 기반으로 동적 메타 태그 생성
 * @param {Object} battle - 배틀 데이터
 * @returns {Object} OG 메타데이터
 */
export const generateBattleMetadata = (battle) => {
  if (!battle) {
    return {
      title: 'AI Fighter - AI vs AI 실시간 토론 배틀',
      description: 'AI 에이전트들이 벌이는 실시간 토론 배틀을 관전하세요! 최대 5명의 AI가 참여하는 멀티 토론.',
      image: '/cover.png',
      url: 'https://aifighter.io',
    };
  }

  // 멀티파이터 지원
  const fighters = battle.fighters || [];
  let fighterNames;
  if (fighters.length > 0) {
    fighterNames = fighters.map((f) => f?.name || '').filter(Boolean).join(' vs ');
  } else {
    fighterNames = `${battle.fighter_a?.name || battle.fighter_a || ''} vs ${battle.fighter_b?.name || battle.fighter_b || ''}`;
  }

  const statusText = battle.status === 'completed' ? '완료' : '진행 중';

  return {
    title: `${fighterNames} - AI Fighter 토론 배틀`,
    description: `주제: "${battle.topic}" | ${battle.rounds}라운드 토론 | ${statusText}`,
    image: '/cover.png',
    url: `https://aifighter.io/battles/${battle.id}`,
    shareCode: battle.share_code,
  };
};

/**
 * 메타 태그를 동적으로 업데이트
 * @param {Object} metadata - 메타데이터 객체
 */
export const updateMetaTags = (metadata) => {
  // OG 태그 업데이트
  updateOrCreateMetaTag('og:title', metadata.title, 'property');
  updateOrCreateMetaTag('og:description', metadata.description, 'property');
  updateOrCreateMetaTag('og:image', metadata.image, 'property');
  updateOrCreateMetaTag('og:url', metadata.url, 'property');

  // Twitter 태그 업데이트
  updateOrCreateMetaTag('twitter:title', metadata.title, 'name');
  updateOrCreateMetaTag('twitter:description', metadata.description, 'name');
  updateOrCreateMetaTag('twitter:image', metadata.image, 'name');

  // 페이지 제목 업데이트
  document.title = metadata.title;

  // description 태그 업데이트
  updateOrCreateMetaTag('description', metadata.description, 'name');
};

/**
 * 메타 태그 생성 또는 업데이트
 * @param {string} name - 태그 이름
 * @param {string} content - 태그 내용
 * @param {string} attr - 속성 (property 또는 name)
 */
const updateOrCreateMetaTag = (name, content, attr = 'name') => {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

/**
 * 배틀 공유 가능한 링크 생성
 * @param {string} battleId - 배틀 ID
 * @param {string} shareCode - 공유 코드
 * @returns {string} 공유 링크
 */
export const generateShareLink = (battleId, shareCode) => {
  if (shareCode) {
    return `https://aifighter.io/battle/share/${shareCode}`;
  }
  return `https://aifighter.io/battles/${battleId}`;
};

/**
 * SNS별 공유 URL 생성
 * @param {Object} options - 공유 옵션
 * @returns {Object} SNS별 공유 링크
 */
export const generateShareUrls = ({
  url,
  title,
  description,
  hashtags = '#AI토론 #AIFighter',
}) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  return {
    // 카카오톡
    kakao: `https://share.kakao.com/web/shareLink?appId=${process.env.REACT_APP_KAKAO_APP_ID}&appVersion=1.0&appName=AI%20Fighter&message=${encodedDescription}&link=${encodedUrl}`,

    // 페이스북
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,

    // 트위터
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&hashtags=AI토론,AIFighter`,

    // 라인
    line: `https://line.me/R/msg/text/${encodedDescription}%0A${encodedUrl}`,

    // 텔레그램
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,

    // 이메일
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,

    // URL 복사
    copy: url,
  };
};

/**
 * 클립보드에 복사
 * @param {string} text - 복사할 텍스트
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('복사 실패:', error);
    return false;
  }
};
